import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, Code, MonitorSmartphone, Search, X, Sparkles } from "lucide-react";

import AppsProducts from "./apps-products";
import SoftwareProducts from "./software-products";
import WebsiteProducts from "./website-products";
import { useAdmin } from "../../context/AdminContext";


export default function OurProductsAll() {
  const { apps, software, websites } = useAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const dynamicTabs = [
    { id: "apps", label: "Apps", icon: MonitorSmartphone, component: AppsProducts, data: apps },
    { id: "software", label: "Software", icon: LayoutGrid, component: SoftwareProducts, data: software },
    { id: "websites", label: "Websites", icon: Code, component: WebsiteProducts, data: websites },
  ];

  const initialTab = searchParams.get("tab") || "apps";
  const initialSearch = searchParams.get("search") || "";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState(initialSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const urlTab = searchParams.get("tab") || "apps";
    const urlSearch = searchParams.get("search") || "";
    setActiveTab(urlTab);
    setSearchQuery(urlSearch);
  }, [searchParams]);

  useEffect(() => {
    if (isInitialLoading) return;

    const delayDebounceFn = setTimeout(() => {
      const params: any = {};
      if (activeTab !== "apps") params.tab = activeTab;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      setSearchParams(params, { replace: true });
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, activeTab, setSearchParams, isInitialLoading]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const params: any = {};
    if (tabId !== "apps") params.tab = tabId;
    if (searchQuery.trim()) params.search = searchQuery.trim();
    setSearchParams(params, { replace: true });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    const params: any = {};
    if (activeTab !== "apps") params.tab = activeTab;
    setSearchParams(params, { replace: true });
  };

  const filterDataset = (dataset: any[]) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return dataset;
    return dataset.filter(
      (item) =>
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.tags?.some((tag: string) => tag.toLowerCase().includes(query)) ||
        item.features?.some((feat: string) => feat.toLowerCase().includes(query))
    );
  };

  const currentTabData = dynamicTabs.find((t) => t.id === activeTab) || dynamicTabs[0];
  const filteredItems = filterDataset(currentTabData.data);
  const SelectedComponent = currentTabData.component;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* HERO SECTION */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={13} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Our Products Ecosystem
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Innovative Digital <span className="text-blue-600 dark:text-blue-400">Products & Deployments</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base font-medium max-w-xl mx-auto">
            Instant real-time access to our mobile apps, SaaS software suites, and website platforms.
          </p>
        </div>

        {/* STICKY SEARCHBAR & FILTER TABS CONTAINER FOR DESKTOP AND MOBILE */}
        <div className="sticky top-[64px] z-40 bg-slate-50/95 dark:bg-[#030712]/95 backdrop-blur-xl py-4 px-4 sm:px-6 border-b border-slate-200 dark:border-slate-800 shadow-md space-y-4 rounded-2xl">
          
          {/* SEARCH BAR */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search across title, tags, or features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isInitialLoading}
              className="w-full pl-11 pr-10 py-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-2xl text-xs font-medium focus:border-blue-600 dark:focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none shadow-sm transition-all"
            />
            {searchQuery && !isInitialLoading && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800 rounded-lg transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* MAIN PRODUCT TABS WITH VIBRANT BLUE & PURE WHITE ACTIVE TAB STYLING */}
          <div className="flex justify-center">
            <div className="flex p-1.5 bg-slate-200/90 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-800 rounded-2xl gap-2 shadow-md">
              {dynamicTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    disabled={isInitialLoading}
                    className={`relative flex items-center gap-2.5 px-4 sm:px-6 py-3 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 cursor-pointer ${
                      isActive
                        ? "bg-blue-600 dark:bg-blue-500 !text-white font-extrabold shadow-md shadow-blue-500/30 dark:shadow-[0_0_20px_rgba(59,130,246,0.4)]"
                        : "text-slate-800 dark:text-slate-200 bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-700 hover:text-blue-700 dark:hover:text-blue-400 border border-slate-300 dark:border-slate-700 font-bold"
                    }`}
                  >
                    <Icon size={16} className={isActive ? "!text-white dark:!text-white" : "text-slate-700 dark:text-slate-300"} />
                    <span className={isActive ? "!text-white dark:!text-white font-extrabold" : "text-slate-800 dark:text-slate-200"}>{tab.label}</span>
                    {isActive && !isInitialLoading && (
                      <motion.div
                        layoutId="activeProductTab"
                        className="absolute inset-0 bg-blue-600 dark:bg-blue-500 rounded-xl -z-10 shadow-md shadow-blue-500/30 border border-blue-600 dark:border-blue-400"
                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* TAB CONTENT GRID */}
        <div>
          <SelectedComponent items={filteredItems} />
        </div>

      </div>
    </div>
  );
}
